import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type {
  ContactFormField,
  FormFieldOption,
  NewContactFormField,
} from "@shared/schema";

const TYPES = [
  "text",
  "email",
  "tel",
  "textarea",
  "select",
  "checkbox",
] as const;

export function AdminFormBuilder() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ContactFormField | null>(null);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    queryKey: ["admin", "form-fields"],
    queryFn: () =>
      api<ContactFormField[]>("/api/admin/form-fields"),
  });

  const reorder = useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) =>
      api("/api/admin/form-fields/reorder", {
        method: "POST",
        body: { order },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "form-fields"] }),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/form-fields/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "form-fields"] }),
  });

  function move(idx: number, delta: -1 | 1) {
    if (!q.data) return;
    const next = [...q.data];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    reorder.mutate(
      next.map((f, i) => ({ id: f.id, sortOrder: i })),
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          {t("admin.formBuilder.title")}
        </h1>
        <button
          type="button"
          onClick={() => {
            setCreating(true);
            setEditing(null);
          }}
          className="btn-primary"
        >
          <Plus size={16} /> {t("admin.formBuilder.newField")}
        </button>
      </div>

      <p className="mt-2 text-sm text-brand-500">
        Itt lehet a kapcsolat-form mezőit szerkeszteni. A változtatás azonnal
        érvényes a public oldalon.
      </p>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="card overflow-hidden">
          {q.isLoading && (
            <div className="p-6 text-brand-500">{t("common.loading")}</div>
          )}
          {q.data && q.data.length === 0 && (
            <div className="p-6 text-brand-500">Még nincsenek mezők.</div>
          )}
          <ul className="divide-y divide-brand-100">
            {q.data?.map((f, idx) => (
              <li
                key={f.id}
                className={`p-4 flex items-center gap-3 ${
                  editing?.id === f.id ? "bg-brand-50" : ""
                }`}
              >
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="text-brand-400 hover:text-brand-700 disabled:opacity-30"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === (q.data?.length ?? 0) - 1}
                    className="text-brand-400 hover:text-brand-700 disabled:opacity-30"
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(f);
                    setCreating(false);
                  }}
                  className="flex-1 text-left"
                >
                  <div className="font-medium text-sm">
                    {f.label}{" "}
                    {f.required && (
                      <span className="text-red-500 ml-0.5">*</span>
                    )}
                    {!f.isActive && (
                      <span className="ml-2 text-xs bg-brand-100 text-brand-500 px-1.5 py-0.5 rounded">
                        inaktív
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-brand-500 mt-0.5">
                    <span className="font-mono">{f.key}</span> ·{" "}
                    {t(`admin.formBuilder.types.${f.type}`)}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Törli: ${f.label}?`)) del.mutate(f.id);
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          {(editing || creating) && (
            <FieldEditor
              key={editing?.id ?? "new"}
              field={editing}
              onSaved={() => {
                setEditing(null);
                setCreating(false);
                qc.invalidateQueries({ queryKey: ["admin", "form-fields"] });
              }}
              onCancel={() => {
                setEditing(null);
                setCreating(false);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FieldEditor({
  field,
  onSaved,
  onCancel,
}: {
  field: ContactFormField | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const isNew = !field;
  const [draft, setDraft] = useState<Partial<NewContactFormField>>({
    key: field?.key ?? "",
    label: field?.label ?? "",
    placeholder: field?.placeholder ?? "",
    helpText: field?.helpText ?? "",
    type: field?.type ?? "text",
    required: field?.required ?? false,
    options: field?.options ?? [],
    isActive: field?.isActive ?? true,
    sortOrder: field?.sortOrder ?? 0,
  });
  const [optionsText, setOptionsText] = useState(
    (field?.options ?? [])
      .map((o) => `${o.value}|${o.label}`)
      .join("\n"),
  );

  const save = useMutation({
    mutationFn: () => {
      const opts: FormFieldOption[] =
        draft.type === "select"
          ? optionsText
              .split("\n")
              .map((l) => l.trim())
              .filter(Boolean)
              .map((line) => {
                const [value, ...rest] = line.split("|");
                return {
                  value: value.trim(),
                  label: (rest.join("|").trim() || value).trim(),
                };
              })
          : [];
      const body = { ...draft, options: opts };
      return isNew
        ? api("/api/admin/form-fields", { method: "POST", body })
        : api(`/api/admin/form-fields/${field!.id}`, {
            method: "PATCH",
            body,
          });
    },
    onSuccess: onSaved,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="card p-6 space-y-4"
    >
      <h3 className="font-semibold">
        {isNew ? t("admin.formBuilder.newField") : draft.label || draft.key}
      </h3>

      <div>
        <label className="label">{t("admin.formBuilder.key")} *</label>
        <input
          required
          pattern="[a-z][a-z0-9_]*"
          className="input font-mono"
          value={draft.key ?? ""}
          onChange={(e) => setDraft({ ...draft, key: e.target.value })}
        />
      </div>

      <div>
        <label className="label">{t("admin.formBuilder.label")} *</label>
        <input
          required
          className="input"
          value={draft.label ?? ""}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
        />
      </div>

      <div>
        <label className="label">{t("admin.formBuilder.type")}</label>
        <select
          className="input"
          value={draft.type ?? "text"}
          onChange={(e) =>
            setDraft({
              ...draft,
              type: e.target.value as NewContactFormField["type"],
            })
          }
        >
          {TYPES.map((tp) => (
            <option key={tp} value={tp}>
              {t(`admin.formBuilder.types.${tp}`)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="label">{t("admin.formBuilder.placeholder")}</label>
        <input
          className="input"
          value={draft.placeholder ?? ""}
          onChange={(e) => setDraft({ ...draft, placeholder: e.target.value })}
        />
      </div>

      <div>
        <label className="label">{t("admin.formBuilder.helpText")}</label>
        <input
          className="input"
          value={draft.helpText ?? ""}
          onChange={(e) => setDraft({ ...draft, helpText: e.target.value })}
        />
      </div>

      {draft.type === "select" && (
        <div>
          <label className="label">{t("admin.formBuilder.options")}</label>
          <textarea
            rows={5}
            className="input font-mono text-xs"
            placeholder="value|Label\nvalue2|Label 2"
            value={optionsText}
            onChange={(e) => setOptionsText(e.target.value)}
          />
          <p className="mt-1 text-xs text-brand-500">
            Soronként egy opció: <code>érték|címke</code>
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.required ?? false}
            onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
          />
          {t("admin.formBuilder.required")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.isActive ?? true}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
          />
          {t("admin.formBuilder.isActive")}
        </label>
      </div>

      {save.isError && (
        <div className="text-sm text-red-600">
          {save.error instanceof ApiError
            ? save.error.message
            : "Mentési hiba"}
        </div>
      )}

      <div className="flex gap-2">
        <button type="submit" disabled={save.isPending} className="btn-primary">
          {save.isPending ? "Mentés..." : "Mentés"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Mégse
        </button>
      </div>
    </form>
  );
}
